/**
 * Support chat widget entry point.
 *
 * Loaded on public Razor pages by Controls/Mvc/SupportChatWidget.cs:
 *  - DEBUG:   <script type="module" src="/support-chat/main.ts"> (Vite dev server via the backend proxy)
 *  - RELEASE: hashed bundle resolved from ClientApp/dist/manifest.json
 *
 * Independent from the SPA bootstrap (main-base.ts) on purpose: no
 * PowerDuck, no global CSS chain, no Temporal polyfill — just Vue + widget.
 */
import { createApp } from 'vue';
import './support-chat.css';
import Widget from './components/widget';
import { readConfig } from './config';
import { loadLanguage, resolveLanguage } from './i18n';

const CONTAINER_ID = 'inv-support-chat';

function domReady(): Promise<void> {
	if (document.readyState !== 'loading') {
		return Promise.resolve();
	}

	return new Promise((resolve) => document.addEventListener('DOMContentLoaded', () => resolve(), { once: true }));
}

/**
 * RELEASE emits the widget stylesheet with media="print" so it does not block the page
 * (SupportChatWidget.cs); the widget mounts only once the sheet is in, so the launcher
 * never shows unstyled. In DEBUG Vite injects the CSS itself and there is no such link.
 */
function stylesReady(): Promise<void> {
	const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[data-isc-css]'));
	return Promise.all(
		links.map((link) => {
			if (link.sheet) {
				return undefined;
			}

			return new Promise<void>((resolve) => {
				link.addEventListener('load', () => resolve(), { once: true });
				link.addEventListener('error', () => resolve(), { once: true });
			});
		}),
	).then(() => undefined);
}

async function mount() {
	// a failed pack download falls back to the bundled Slovak texts
	const language = loadLanguage(resolveLanguage(readConfig().language)).catch(() => undefined);
	await Promise.all([domReady(), stylesReady(), language]);
	if (document.getElementById(CONTAINER_ID)) {
		return;
	}

	const host = document.createElement('div');
	host.id = CONTAINER_ID;
	document.body.appendChild(host);
	createApp(Widget).mount(host);
}

void mount();
